import { logger } from 'lib/logger';
import { weblinksIncomingApiCall } from 'reactApp/api/weblinks/WeblinksIncomingApiCall';
import { weblinksIncomingRemoveApiCall } from 'reactApp/api/weblinks/WeblinksIncomingRemoveApiCall';
import { IS_ALL_DOC_SHARED_SORT_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/alldocumentsSortEnabled';
import { renderRemoveDialog } from 'reactApp/components/RemoveDialog/RemoveDialog.helpers';
import { getAllDocumentsExtensions } from 'reactApp/modules/allDocuments/allDocuments.selectors';
import { getIncomingPublicCursor } from 'reactApp/modules/incomingPublic/incomingPublic.selectors';
import { removeFileSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { getSortById } from 'reactApp/modules/sort/sort.selectors';
import { ESortTypes } from 'reactApp/modules/sort/sort.types';
import { sendXray } from 'reactApp/utils/ga';
import { captureException } from 'reactApp/utils/tracer';
import { channel } from 'redux-saga';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';

import { getLoadMoreLimit } from '../storage/storage.helpers';
import { EStorageType } from '../storage/storage.types';
import {
    loadIncomingPublicRequest,
    loadIncomingPublicSuccess,
    loadMoreIncomingPublicRequest,
    removeIncomingPublicRequest,
} from './incomingPublic.module';
import type { LoadIncomingPublicOpts, WeblinksIncomingRemoveParams } from './incomingPublic.types';

const LOAD_LIMIT = getLoadMoreLimit(EStorageType.sharedIncoming);
const REMOVE_PAYLOAD_LIMIT = 300;

const getIncoming = async ({ limit = LOAD_LIMIT, cursor = '', exts = '', sort = undefined }: LoadIncomingPublicOpts) => {
    const replacedMtimeSort = sort?.type ? (sort.type === ESortTypes.mtime ? 'time' : sort.type) : undefined;
    return (await weblinksIncomingApiCall({ limit, cursor, exts, sort: replacedMtimeSort, order: sort?.order })).data;
};

const removeIncomingPublic = async (params: WeblinksIncomingRemoveParams) => (await weblinksIncomingRemoveApiCall(params)).data;

const sendLogsOnError = (haveSort: boolean, type: string, error) => {
    logger.error(error);
    if (haveSort) {
        sendXray(`alldocuments_error_sort_incoming-public_${type}_${error.status ?? 'other'}`);
    }
    sendXray('incoming-public-load-error');
    captureException(error, { issueKey: 'Incoming public load error' });
};

export function* handleLoadIncomingPublic() {
    const sort = yield select((state) => getSortById(state, EStorageType.incomingPublic));
    const haveSort = IS_ALL_DOC_SHARED_SORT_ENABLED ? sort : undefined;

    try {
        const extensions = yield select(getAllDocumentsExtensions);

        const data = yield call(getIncoming, { limit: LOAD_LIMIT, cursor: '', exts: extensions.join(','), sort: haveSort });
        yield put(loadIncomingPublicSuccess(data));
    } catch (error) {
        sendLogsOnError(haveSort, sort.type, error);
    }
}

function* handleLoadMoreIncomingPublic() {
    const cursor = yield select(getIncomingPublicCursor);

    const extensions = yield select(getAllDocumentsExtensions);
    const sort = yield select((state) => getSortById(state, EStorageType.incomingPublic));
    const haveSort = IS_ALL_DOC_SHARED_SORT_ENABLED ? sort : undefined;

    try {
        const data = yield call(getIncoming, {
            limit: LOAD_LIMIT,
            cursor,
            exts: extensions.join(','),
            sort: haveSort,
        });

        yield put(loadIncomingPublicSuccess(data));
    } catch (error) {
        sendLogsOnError(haveSort, sort.type, error);
    }
}

function* handleIncomingPublicRemove({ payload }: ReturnType<typeof removeIncomingPublicRequest>) {
    try {
        const deleteChannel = channel();

        const dialog = renderRemoveDialog({
            items: payload.weblink_ids,
            onClose: () => {
                deleteChannel.close();
            },
            onRemove: () => {
                deleteChannel.put(true);
            },
        });

        yield take(deleteChannel);
        yield deleteChannel.close();
        if (dialog) {
            yield call(dialog.closeDialog);
        }

        if (!payload.weblink_ids) {
            return;
        }

        for (let i = 0; i < payload.weblink_ids.length; i += REMOVE_PAYLOAD_LIMIT) {
            const chunk = payload.weblink_ids.slice(i, i + REMOVE_PAYLOAD_LIMIT);
            yield call(removeIncomingPublic, {
                weblink_ids: chunk,
            });
        }

        yield put(removeFileSuccess({ ids: payload.weblink_ids }));

        yield call(sendXray, 'incoming-public-remove-success');
    } catch (error) {
        logger.error(error);
        sendXray('incoming-public-remove-error');
        captureException(error, { issueKey: 'Incoming public remove error' });
    }
}

export function* watchIncomingPublic() {
    yield takeEvery(loadIncomingPublicRequest.toString(), handleLoadIncomingPublic);
    yield takeEvery(loadMoreIncomingPublicRequest.toString(), handleLoadMoreIncomingPublic);
    yield takeEvery(removeIncomingPublicRequest.toString(), handleIncomingPublicRemove);
}
